import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Center,
  HStack,
  Heading,
  Image,
  Spinner,
  Step,
  StepIcon,
  StepIndicator,
  StepNumber,
  StepSeparator,
  StepStatus,
  StepTitle,
  Stepper,
  VStack,
  useSteps,
  useToast,
} from "@chakra-ui/react";
import {
  NewCOCStep0,
  NewCOCStep1,
  NewCOCStep2,
  NewCOCStep3,
  NewOrderEntryGate,
} from "../../components";
import { useNavigate } from "react-router-dom";
import { AuthModel, CocModel, UserModel } from "../../services/models";
import { useFetchOrderIntegrations } from "../../hooks/useFetchOrderIntegrations";

export default function NewCOC() {
  const user = AuthModel.getUser();
  const initialFormData = {
    userId: "",
    refId: "",
    clientName: "",
    contactName: "",
    email: "",
    phone: "",
    samplingFor: "",
    dateSampled: "",
    samplingTechnique: "",
    securitySealNo: "",
    propertyType: "commercial",
    address: "",
    city: "",
    state: "",
    postcode: "",
    rego: "",
    regoState: "",
    VIN: "",
    samples: [
      {
        value: "",
        methylamphetamine: { value: "", color: "" },
        amphetamine: { value: "", color: "" },
        ephedrine: { value: "", color: "" },
        pseudoephedrine: { value: "", color: "" },
        mdma: { value: "", color: "" },
        fentanyl: { value: "", color: "" },
        cocaine: { value: "", color: "" },
      },
    ],
  };
  const [formData, setFormData] = useState(initialFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const toast = useToast();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const externalIntegrationsForUser = user.role === "user";
  const { integrations, loading: integrationsLoading } =
    useFetchOrderIntegrations(externalIntegrationsForUser);
  const [orderEntryMode, setOrderEntryMode] = useState(null);
  const onApplyExternalImport = externalIntegrationsForUser
    ? (merged) => setFormData(merged)
    : undefined;

  const handleAnotherOrder = () => {
    setFormData(initialFormData);
    setIsSubmitting(false);
    setActiveStep(0);
    setOrderEntryMode(null);
  };

  const onNextStep = (stepData) => {
    let newData = { ...formData, ...stepData };
    console.log(newData);
    setFormData(newData);
    if (user.role !== "admin") {
      if (activeStep === 2) {
        onBusinessInfoSubmit(newData);
      } else {
        setActiveStep(activeStep + 1);
      }
    } else {
      if (activeStep === 3) {
        onBusinessInfoSubmit(newData);
      } else {
        setActiveStep(activeStep + 1);
      }
    }
  };
  const onBackStep = (stepData) => {
    let newData = { ...formData, ...stepData };
    setFormData(newData);
    setActiveStep(activeStep - 1);
  };

  const onBusinessInfoSubmit = async (body) => {
    setIsSubmitting(true);
    try {
      body.dateReceived = "";
      body.dateAnalysed = "";
      body.status = "pending";
      body.dateSampled = body.dateSampled;
      body.questions = [];
      body.reportType = "manual";
      body.pdfFile = "";
      if (user.role !== "admin") {
        body.userId = user._id;
      }
      if (user.role === "user") {
        const r = body.refId;
        body.refId =
          r == null || String(r).trim() === "" ? "" : String(r).trim();
      }

      let response = await CocModel.create(body);
      if (response.status === 200) {
        setActiveStep(activeStep + 1);
      }
    } catch (error) {
      toast({
        description: "Something went wrong!",
        status: "error",
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        let response = await UserModel.getAllByRole("user");
        if (response.data.data && response.data.data.length) {
          setUsers(
            response.data.data.map((item) => ({
              label: item.firstName + " " + item.lastName,
              value: item._id,
              email: item.email,
            }))
          );
        }
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    };
    fetchUsers();
  }, []);

  const clientSource =
    orderEntryMode === "import" ? "external" : "manual";

  const steps =
    user.role !== "admin"
      ? [
          {
            title: "Client Information",
            content: (
              <NewCOCStep1
                onNextStep={onNextStep}
                onBackStep={onBackStep}
                formData={formData}
                user={user}
                onApplyImport={onApplyExternalImport}
                clientSource={clientSource}
                integrations={integrations}
              />
            ),
          },
          {
            title: "Sampling Details",
            content: (
              <NewCOCStep2
                onNextStep={onNextStep}
                onBackStep={onBackStep}
                formData={formData}
              />
            ),
          },
          {
            title: "Samples Information",
            content: (
              <NewCOCStep3
                onNextStep={onNextStep}
                onBackStep={onBackStep}
                formData={formData}
                isSubmitting={isSubmitting}
              />
            ),
          },
        ]
      : [
          {
            title: "Select Client",
            content: (
              <NewCOCStep0
                onNextStep={onNextStep}
                formData={formData}
                users={users}
              />
            ),
          },
          {
            title: "Client Information",
            content: (
              <NewCOCStep1
                onNextStep={onNextStep}
                onBackStep={onBackStep}
                formData={formData}
                user={user}
                onApplyImport={onApplyExternalImport}
                clientSource={clientSource}
                integrations={integrations}
              />
            ),
          },
          {
            title: "Sampling Details",
            content: (
              <NewCOCStep2
                onNextStep={onNextStep}
                onBackStep={onBackStep}
                formData={formData}
              />
            ),
          },
          {
            title: "Samples Information",
            content: (
              <NewCOCStep3
                onNextStep={onNextStep}
                onBackStep={onBackStep}
                formData={formData}
                isSubmitting={isSubmitting}
              />
            ),
          },
        ];
  const { activeStep, setActiveStep } = useSteps({
    index: 0,
    count: steps.length,
  });

  const stepCondition = user.role !== "admin" ? activeStep < 3 : activeStep < 4;
  const hasFetchIntegrations = integrations.length > 0;
  const showEntryGate =
    stepCondition &&
    !integrationsLoading &&
    hasFetchIntegrations &&
    orderEntryMode === null;

  return (
    <>
      <Heading fontSize={{ base: "xl", md: "3xl" }}>
        Illicit Drugs On Wipes Digital Chain Of Custody Form
      </Heading>
      {stepCondition ? (
        integrationsLoading ? (
          <Center my={5}>
            <Spinner />
          </Center>
        ) : showEntryGate ? (
          <NewOrderEntryGate
            onCreateNew={() => setOrderEntryMode("create")}
            onImportExternal={() => setOrderEntryMode("import")}
          />
        ) : user.role === "admin" && loading ? (
          <Center my={5}>
            <Spinner />
          </Center>
        ) : (
          <>
            <Center my="4">
              <Stepper
                index={activeStep}
                w={user.role !== "admin" ? "650px" : "850px"}
              >
                {steps.map((step, index) => (
                  <Step key={index}>
                    <StepIndicator
                      sx={
                        index <= activeStep
                          ? {
                              background: "#A67C52 !important",
                              color: "white !important",
                            }
                          : {
                              background: "lightgray",
                              color: "white",
                            }
                      }
                    >
                      <StepStatus
                        complete={<StepIcon />}
                        incomplete={<StepNumber />}
                        active={<StepNumber />}
                      />
                    </StepIndicator>

                    <Box flexShrink="0" display={{ base: "none", md: "block" }}>
                      <StepTitle fontWeight="600" fontSize="14px">
                        {step.title}
                      </StepTitle>
                    </Box>

                    <StepSeparator
                      sx={
                        index < activeStep
                          ? {
                              background: "#A67C52 !important",
                            }
                          : {
                              background: "lightgray !important",
                            }
                      }
                    />
                  </Step>
                ))}
              </Stepper>
            </Center>
            <Box>{steps[activeStep].content}</Box>
          </>
        )
      ) : (
        <Center h="90%">
          <VStack>
            <Image
              src="/images/new-coc-img2.png"
              alt=""
              w={{ base: "50%", lg: "20%" }}
            />
            <Heading>Order Submitted!</Heading>
            <HStack
              gap="3"
              flexWrap="wrap"
              justifyContent="center"
              alignItems="center"
            >
              <Button
                bgColor="black"
                borderRadius="lg"
                fontWeight="medium"
                py="6"
                px="7"
                onClick={handleAnotherOrder}
              >
                Another Order
              </Button>
              <Button
                bgColor="black"
                borderRadius="lg"
                fontWeight="medium"
                py="6"
                px="7"
                onClick={() =>
                  user.role === "admin"
                    ? navigate("/")
                    : navigate("/all-orders")
                }
              >
                All Orders
              </Button>
            </HStack>
          </VStack>
        </Center>
      )}
    </>
  );
}
